import type { Observation } from "@medplum/fhirtypes";

import {
  LABOR_AMEND_BIRTH_LOAD_FAILED_MESSAGE,
  LABOR_AMEND_BIRTH_LOAD_FAILED_TITLE,
  LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_MESSAGE,
  LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_TITLE,
  LABOR_AMEND_PERSIST_LOCKED_MESSAGE,
  LABOR_AMEND_PERSIST_LOCKED_TITLE,
  LABOR_AMEND_QUEUED_TITLE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import type {
  BirthObservationsLoadStatus,
  LaborStagesLoadStatus,
} from "@/encounters/components/EncounterLabor/utils/labor-amend-sign-and-lock-ready";
import type {
  EnqueueFlowsheetPersistOptions,
  LaborAmendPendingDisplayEntry,
} from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import { toast } from "@/utils/notifications";

export interface EnqueueLaborAmendPersistParams {
  deferFlowsheetPersists: boolean;
  isFlowsheetEditingEnabled?: boolean;
  laborStagesLoadStatus?: LaborStagesLoadStatus;
  birthObservationsLoadStatus?: BirthObservationsLoadStatus;
  isFlowsheetPersistLocked?: boolean;
  enqueueFlowsheetPersist: (
    job: () => Promise<void>,
    options?: EnqueueFlowsheetPersistOptions,
  ) => boolean;
  registerPendingDisplayEntry?: (entry: LaborAmendPendingDisplayEntry) => void;
  setObservationPreviewPatch?: (observationId: string, patch: Partial<Observation>) => void;
  pendingDisplayEntry?: LaborAmendPendingDisplayEntry;
  observationPreviewPatch?: { observationId: string; patch: Partial<Observation> };
  replaceKey?: string;
  persist: () => Promise<void>;
  queuedMessage: string;
  errorTitle: string;
  errorMessage: string;
  onQueued?: () => void;
}

/** Per-call options; shared amend persist context is injected by `useEnqueueLaborAmendPersist`. */
export type EnqueueLaborAmendPersistCallOptions = Pick<
  EnqueueLaborAmendPersistParams,
  | "pendingDisplayEntry"
  | "observationPreviewPatch"
  | "replaceKey"
  | "persist"
  | "queuedMessage"
  | "errorTitle"
  | "errorMessage"
  | "onQueued"
>;

/**
 * When amend defer mode is active, registers preview state, enqueues the persist job, and shows the
 * standard queued toast. Returns true when the caller should stop (defer handled).
 */
function getFlowsheetEditingBlockedToast({
  laborStagesLoadStatus = "ready",
  birthObservationsLoadStatus = "ready",
}: {
  laborStagesLoadStatus?: LaborStagesLoadStatus;
  birthObservationsLoadStatus?: BirthObservationsLoadStatus;
}): { title: string; message: string } {
  if (laborStagesLoadStatus !== "failed" && birthObservationsLoadStatus === "failed") {
    return {
      title: LABOR_AMEND_BIRTH_LOAD_FAILED_TITLE,
      message: LABOR_AMEND_BIRTH_LOAD_FAILED_MESSAGE,
    };
  }

  return {
    title: LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_TITLE,
    message: LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_MESSAGE,
  };
}

function showPersistLockedToast(): void {
  toast({
    title: LABOR_AMEND_PERSIST_LOCKED_TITLE,
    message: LABOR_AMEND_PERSIST_LOCKED_MESSAGE,
    type: "warning",
  });
}

export function enqueueLaborAmendPersist({
  deferFlowsheetPersists,
  isFlowsheetEditingEnabled = true,
  laborStagesLoadStatus = "ready",
  birthObservationsLoadStatus = "ready",
  isFlowsheetPersistLocked = false,
  enqueueFlowsheetPersist,
  registerPendingDisplayEntry,
  setObservationPreviewPatch,
  pendingDisplayEntry,
  observationPreviewPatch,
  replaceKey,
  persist,
  queuedMessage,
  errorTitle,
  errorMessage,
  onQueued,
}: EnqueueLaborAmendPersistParams): boolean {
  if (!deferFlowsheetPersists) {
    return false;
  }

  if (!isFlowsheetEditingEnabled) {
    const blockedToast = getFlowsheetEditingBlockedToast({
      laborStagesLoadStatus,
      birthObservationsLoadStatus,
    });
    toast({
      title: blockedToast.title,
      message: blockedToast.message,
      type: "error",
      autoClose: false,
    });
    return true;
  }

  if (isFlowsheetPersistLocked) {
    showPersistLockedToast();
    return true;
  }

  const didEnqueue = enqueueFlowsheetPersist(
    async () => {
      try {
        await persist();
      } catch (error) {
        console.error(error);
        toast({
          title: errorTitle,
          message: error instanceof Error ? error.message : errorMessage,
          type: "error",
          autoClose: false,
        });
        throw error;
      }
    },
    { replaceKey },
  );

  if (!didEnqueue) {
    showPersistLockedToast();
    return true;
  }

  if (pendingDisplayEntry && registerPendingDisplayEntry) {
    registerPendingDisplayEntry(pendingDisplayEntry);
  }

  if (observationPreviewPatch && setObservationPreviewPatch) {
    setObservationPreviewPatch(
      observationPreviewPatch.observationId,
      observationPreviewPatch.patch,
    );
  }

  toast({
    title: LABOR_AMEND_QUEUED_TITLE,
    message: queuedMessage,
    type: "info",
  });

  onQueued?.();
  return true;
}
